import { useQueryStates } from 'src/core/api/hooks/useQueryStates';
import { type QueryState } from 'src/core/api/queryState';

import { useBudgetQuery } from '../../../../containers/BudgetOverviewPageContainer/hooks/useBudgetQuery';
import { useBreakdownQuery } from '../../../../containers/hooks/useBreakdownQuery';
import { useExpenseCategoriesBreakdownQuery } from '../../../../hooks/useExpenseCategoriesBreakdownQuery';
import {
  getSpentBreakdownRatio,
  isBudgetBreakdownEmpty,
} from '../../../../models/breakdown';
import { type ExpenseCategoryBreakdown } from '../../../../models/expenseCategoryBreakdown';
import { type PeriodRangePayload } from '../../../../models/periodRange';

export type BudgetListItem = {
  id: string;
  name: string;
  usedRatio: number;
  isSelected: boolean;
  isArchived?: boolean;
  subBudgets: BudgetListItem[];
};

export const useBudgetsOverviewList = ({
  budgetId,
  periodRange,
}: {
  budgetId: string;
  periodRange: PeriodRangePayload;
}): QueryState<BudgetListItem[]> => {
  const queryStates = useQueryStates({
    states: {
      budget: useBudgetQuery(budgetId),
      breakdown: useBreakdownQuery(budgetId, periodRange),
      expenseCategoriesBreakdown: useExpenseCategoriesBreakdownQuery(
        budgetId,
        periodRange,
      ),
    },
  });

  if (queryStates.status === 'success') {
    return {
      status: 'success',
      data: [
        {
          id: queryStates.data.budget.id,
          name: queryStates.data.budget.name,
          usedRatio: getSpentBreakdownRatio(queryStates.data.breakdown),
          isSelected: false,
          subBudgets:
            queryStates.data.expenseCategoriesBreakdown.expenseCategories
              .map((expenseCategory) => {
                const expenseCategoryBreakdown =
                  queryStates.data.expenseCategoriesBreakdown.expenseCategoriesBreakdown.find(
                    (ecb) => ecb.expenseCategory.id === expenseCategory.id,
                  );
                return {
                  id: expenseCategory.id,
                  name: expenseCategory.name,
                  usedRatio: expenseCategoryBreakdown
                    ? getSpentBreakdownRatio(expenseCategoryBreakdown)
                    : 0,
                  isSelected: false,
                  isArchived: expenseCategory.isArchived,
                  subBudgets: [],
                };
              })
              .filter((expenseCategory) => {
                const expenseCategoryBreakdown =
                  queryStates.data.expenseCategoriesBreakdown.expenseCategoriesBreakdown.find(
                    (ecb) => ecb.expenseCategory.id === expenseCategory.id,
                  );
                return (
                  expenseCategoryBreakdown &&
                  shouldDisplayBreakdown(expenseCategoryBreakdown)
                );
              }),
        },
      ],
    };
  }

  return queryStates;
};

const shouldDisplayBreakdown = (breakdown: ExpenseCategoryBreakdown): boolean =>
  !isBudgetBreakdownEmpty(breakdown) &&
  breakdown.expenseCategory.id !== 'default';
